import { useActiveTenant } from "../../../auth/AuthProvider";
import { getFontCss, getFontUrl } from "./fontStacks";

const DEFAULT_PRIMARY = "#2663C7";

/**
 * Pick a legible text color (near-black or white) for a label sitting on top
 * of an arbitrary brand color, based on perceived luminance.
 */
function readableText(hex: string): string {
  const h = hex.trim().replace("#", "");
  if (!/^[0-9A-Fa-f]{6}$/.test(h)) return "#ffffff";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#1a1a1a" : "#ffffff";
}

interface BrandingPreviewProps {
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  backgroundColor: string | null;
  tertiaryColor?: string | null;
  primaryBgColor?: string | null;
  secondaryBgColor?: string | null;
  tertiaryBgColor?: string | null;
  headingFont?: string | null;
  bodyFont?: string | null;
}

/**
 * Consolidated live preview shown beside the branding edit forms. Pulls the
 * logo, favicon, and brand colors into one representative mock so the brand
 * can be judged as a whole. Reflects the same branding values the page holds,
 * so it tracks edits as they save.
 */
export function BrandingPreview({
  logoUrl,
  faviconUrl,
  primaryColor,
  secondaryColor,
  backgroundColor,
  tertiaryColor,
  primaryBgColor,
  secondaryBgColor,
  tertiaryBgColor,
  headingFont,
  bodyFont,
}: BrandingPreviewProps) {
  const { domain, name: brandName } = useActiveTenant();

  const primary = primaryColor || DEFAULT_PRIMARY;
  const secondary = secondaryColor || primary;
  const headerBg = backgroundColor || primary;

  const headingFontCss = getFontCss(headingFont) ?? undefined;
  const bodyFontCss = getFontCss(bodyFont) ?? undefined;

  // Load Google Fonts for preview
  const fontUrls = new Set<string>();
  const hUrl = getFontUrl(headingFont);
  const bUrl = getFontUrl(bodyFont);
  if (hUrl) fontUrls.add(hUrl);
  if (bUrl) fontUrls.add(bUrl);

  return (
    <aside className="lg:sticky lg:top-4">
      {/* Load Google Fonts for preview */}
      {[...fontUrls].map((url) => (
        <link key={url} rel="stylesheet" href={url} />
      ))}
      <div className="shadow-card overflow-hidden rounded-lg bg-paper-hi">
        <div className="hair-b border-b px-4 py-3">
          <h3 className="text-[14px] font-semibold">Preview</h3>
          <p className="mt-0.5 text-[11.5px] text-ink-4">
            How your brand looks to customers
          </p>
        </div>

        <div className="space-y-5 p-4" style={{ fontFamily: bodyFontCss }}>
          {/* Browser frame: favicon + domain in the address bar */}
          <div className="daylight hair overflow-hidden rounded-lg border bg-paper">
            <div className="hair-b border-b flex items-center gap-2 bg-paper-2 px-3 py-2">
              <div className="flex gap-1">
                <span className="h-2 w-2 rounded-full bg-ink-5/50" />
                <span className="h-2 w-2 rounded-full bg-ink-5/50" />
                <span className="h-2 w-2 rounded-full bg-ink-5/50" />
              </div>
              <div className="hair flex flex-1 items-center gap-1.5 rounded border bg-paper-hi px-2 py-1">
                {faviconUrl ? (
                  <img
                    src={faviconUrl}
                    alt=""
                    className="h-3 w-3 rounded-sm object-contain"
                  />
                ) : (
                  <span className="h-3 w-3 rounded-sm bg-ink-5/30" />
                )}
                <span className="truncate font-mono text-[10.5px] text-ink-4">
                  {domain}
                </span>
              </div>
            </div>

            {/* Header band on the background color, carrying the logo */}
            <div
              className="flex h-20 items-center justify-center px-4"
              style={{ backgroundColor: headerBg }}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="max-h-12 max-w-[70%] object-contain"
                />
              ) : (
                <span
                  className="font-display text-[20px] font-medium tracking-tight"
                  style={{ color: readableText(headerBg) }}
                >
                  {brandName}
                </span>
              )}
            </div>

            {/* Sample content driven by the brand colors */}
            <div className="space-y-3 p-4">
              {headingFontCss && (
                <div
                  className="text-[15px] font-semibold text-[#2c3e50]"
                  style={{ fontFamily: headingFontCss }}
                >
                  Your Practice Name
                </div>
              )}
              <div className="space-y-1.5">
                <div className="h-2 w-2/3 rounded bg-ink-5/30" />
                <div className="h-2 w-1/2 rounded bg-ink-5/20" />
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="rounded-md px-3 py-1.5 text-[11.5px] font-semibold"
                  style={{ backgroundColor: primary, color: readableText(primary) }}
                >
                  Book now
                </span>
                <span
                  className="rounded-md px-3 py-1.5 text-[11.5px] font-semibold"
                  style={{
                    backgroundColor: secondary,
                    color: readableText(secondary),
                  }}
                >
                  Learn more
                </span>
              </div>
              <div className="text-[11.5px] font-medium" style={{ color: primary }}>
                View available times →
              </div>
            </div>
          </div>

          {/* Section background demo strips */}
          <div className="space-y-1">
            <div className="text-[10px] font-medium uppercase tracking-wider text-ink-4">
              Section backgrounds
            </div>
            <div className="daylight hair overflow-hidden rounded-md border">
              <SectionStrip label="Primary Background" hex={primaryBgColor ?? null} />
              <SectionStrip label="Secondary Background" hex={secondaryBgColor ?? null} />
              <SectionStrip label="Tertiary Background" hex={tertiaryBgColor ?? null} />
            </div>
          </div>

          {/* Exact values */}
          <div className="grid grid-cols-4 gap-2">
            <SwatchChip label="Primary" hex={primaryColor} fallback={DEFAULT_PRIMARY} />
            <SwatchChip label="Secondary" hex={secondaryColor} />
            <SwatchChip label="Tertiary" hex={tertiaryColor ?? null} />
            <SwatchChip label="Background" hex={backgroundColor} fallback={primaryColor} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <SwatchChip label="Primary Background" hex={primaryBgColor ?? null} fallback={backgroundColor} />
            <SwatchChip label="Secondary Background" hex={secondaryBgColor ?? null} />
            <SwatchChip label="Tertiary Background" hex={tertiaryBgColor ?? null} />
          </div>
        </div>
      </div>
    </aside>
  );
}

interface SwatchChipProps {
  label: string;
  hex: string | null;
  // Swatch color used when `hex` is unset (e.g. background inherits primary).
  fallback?: string | null;
}

function SwatchChip({ label, hex, fallback }: SwatchChipProps) {
  const swatch = hex || fallback || null;
  return (
    <div className="space-y-1">
      <div
        className={`hair h-9 rounded-md border ${swatch ? "" : "border-dashed bg-paper"}`}
        style={swatch ? { backgroundColor: swatch } : undefined}
      />
      <div className="text-[10px] font-medium uppercase tracking-wider text-ink-4">
        {label}
      </div>
      <div className="font-mono text-[10px] text-ink-3">
        {hex || (fallback ? "Inherited" : "Not set")}
      </div>
    </div>
  );
}

// ── Email Preview ──────────────────────────────────────────────

interface EmailPreviewProps {
  logoUrl: string | null;
  emailLogoUrl: string | null;
  /** Resolved header background (email override > site bg > site primary > default) */
  headerBgColor: string;
  /** Resolved accent color for buttons/links/borders (email override > site primary > default) */
  accentColor: string;
  /** Resolved highlight box background (email override > site primaryBg > #f0f8ff) */
  highlightBgColor: string;
  /** Resolved callout box background (email override > site secondaryBg > #f9f9f9) */
  calloutBgColor: string;
}

/**
 * Miniature mock of the customer email template (base_customer_email.html).
 * Mirrors the real template structure: branded header band, white body with
 * highlight/callout boxes, dark footer. Receives pre-resolved colors that
 * follow the same override chain as branding_tokens.py.
 */
export function EmailPreview({
  logoUrl,
  emailLogoUrl,
  headerBgColor,
  accentColor,
  highlightBgColor,
  calloutBgColor,
}: EmailPreviewProps) {
  const brandName = useActiveTenant().name;

  const primary = accentColor;
  const headerBg = headerBgColor;
  const highlightBg = highlightBgColor;
  const calloutBg = calloutBgColor;

  // Email logo fallback chain: emailLogoUrl > logoUrl > text wordmark
  const effectiveLogo = emailLogoUrl || logoUrl;

  return (
    <div className="shadow-card overflow-hidden rounded-lg bg-paper-hi">
      <div className="hair-b border-b px-4 py-3">
        <h3 className="text-[14px] font-semibold">Email preview</h3>
        <p className="mt-0.5 text-[11.5px] text-ink-4">
          How branded emails look to customers
        </p>
      </div>

      <div className="p-4">
        <div className="daylight hair overflow-hidden rounded-lg border">
          {/* Header band */}
          <div
            className="flex items-center justify-center px-4 py-4"
            style={{ backgroundColor: headerBg }}
          >
            {effectiveLogo ? (
              <img
                src={effectiveLogo}
                alt="Email logo"
                className="max-h-8 max-w-[60%] object-contain"
              />
            ) : (
              <span
                className="font-display text-[15px] font-medium tracking-tight"
                style={{ color: readableText(headerBg) }}
              >
                {brandName}
              </span>
            )}
          </div>

          {/* Body */}
          <div className="space-y-3 bg-white px-4 py-4">
            {/* Heading */}
            <div className="text-[12px] font-bold text-[#2c3e50]">
              Your Booking is Confirmed
            </div>

            {/* Body text placeholder lines */}
            <div className="space-y-1.5">
              <div className="h-1.5 w-5/6 rounded-full bg-[#4d4d4d]/15" />
              <div className="h-1.5 w-3/4 rounded-full bg-[#4d4d4d]/15" />
              <div className="h-1.5 w-2/3 rounded-full bg-[#4d4d4d]/15" />
            </div>

            {/* Highlight box */}
            <div
              className="rounded-md px-3 py-2.5"
              style={{
                backgroundColor: highlightBg,
                border: `2px solid ${primary}`,
              }}
            >
              <div className="space-y-1">
                <div className="h-1.5 w-2/3 rounded-full bg-[#2c3e50]/20" />
                <div className="h-1.5 w-1/2 rounded-full bg-[#2c3e50]/15" />
              </div>
            </div>

            {/* Callout box */}
            <div
              className="rounded px-3 py-2"
              style={{
                backgroundColor: calloutBg,
                borderLeft: `3px solid ${primary}`,
              }}
            >
              <div className="space-y-1">
                <div className="h-1.5 w-3/5 rounded-full bg-[#2c3e50]/15" />
                <div className="h-1.5 w-2/5 rounded-full bg-[#2c3e50]/10" />
              </div>
            </div>

            {/* CTA button */}
            <div className="pt-1 text-center">
              <span
                className="inline-block rounded-md px-4 py-1.5 text-[10.5px] font-semibold"
                style={{ backgroundColor: primary, color: readableText(primary) }}
              >
                View Booking
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-[#2c3e50] px-4 py-3 text-center">
            <div className="text-[10px] font-semibold text-white">
              {brandName}
            </div>
            <div className="mt-0.5 text-[8.5px] text-[#b0c4d8]">
              Contact info &bull; Address
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * A miniature strip representing a page section painted in a background color.
 * Shows placeholder text lines and a muted label. When no color is set, renders
 * a dashed outline with "Not set" text mirroring SwatchChip's empty state.
 */
function SectionStrip({ label, hex }: { label: string; hex: string | null }) {
  if (!hex) {
    return (
      <div className="hair-b flex items-center justify-center border-b border-dashed bg-paper px-3 py-3 last:border-b-0">
        <span className="text-[10px] text-ink-5">Not set</span>
      </div>
    );
  }

  const textColor = readableText(hex);
  const fadedText = textColor === "#ffffff" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.15)";

  return (
    <div
      className="flex items-center gap-3 px-3 py-3"
      style={{ backgroundColor: hex }}
    >
      <span
        className="shrink-0 text-[10px] font-medium"
        style={{ color: textColor, opacity: 0.7 }}
      >
        {label}
      </span>
      <div className="flex flex-1 flex-col gap-1">
        <div
          className="h-1.5 w-3/4 rounded-full"
          style={{ backgroundColor: fadedText }}
        />
        <div
          className="h-1.5 w-1/2 rounded-full"
          style={{ backgroundColor: fadedText }}
        />
      </div>
    </div>
  );
}
